import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { BehaviorSubject, Observable } from 'rxjs';

import { FeeType } from '@enumerates/fee/fee-type';

import { Fee } from '@models/fee/fee';
import { OneTimeFee } from '@models/fee/one-time/one-time-fee';
import { SubscriptionPlan } from '@models/fee/subscription-plan/subscription-plan';
import { SubscriptionPlanPrice } from '@models/fee/subscription-plan/subscription-plan-price';
import { SelectFee } from '@models/fee/select/select-fee';

import { HttpService } from '@services/http.service';
import { EndpointParams } from '@models/utils/endpoint-params';

@Injectable({
    providedIn: 'root'
})
export class FeesPublicService extends HttpService {
    private readonly defaultEndpointParams: EndpointParams = { app: 'payments', version: 1 };

    private feesSubject = new BehaviorSubject<Fee[]>([]);
    private selectedSubject = new BehaviorSubject<SelectFee>({ idFee: '', idPrice: '' });
    private isLoadingDataSubject = new BehaviorSubject<boolean>(true);
    private dataStore: { fees: Fee[]; selected: SelectFee } = { fees: [], selected: { idFee: '', idPrice: '' } };

    readonly fees = this.feesSubject.asObservable();
    readonly selected = this.selectedSubject.asObservable();
    readonly loadingData = this.isLoadingDataSubject.asObservable();

    list(publicToken: string): void {
        this.loading(true);
        this.listFees(publicToken).subscribe(
            () => {
                this.next();
                this.loading(false);
            }
        );
    }

    setSelected(selected: SelectFee): void {
        this.dataStore.selected = selected;
        this.selectedSubject.next({ ...this.dataStore.selected });
    }

    findFee(idFee: string): Fee {
        const fee = this.dataStore.fees.find(c => c.id === idFee);
        if (fee) {
            return fee;
        } else {
            return new Fee();
        }
    }

    findSubscriptionPlanPrice(selected: SelectFee): SubscriptionPlanPrice {
        const fee = this.dataStore.fees.find(c => c.id === selected.idFee);
        if (fee) {
            const price = fee.prices.find((d: any) => d.id === selected.idPrice);
            if (price) {
                return price;
            } else {
                return new SubscriptionPlanPrice();
            }
        } else {
            return new SubscriptionPlanPrice();
        }
    }

    private listFees(publicToken: string): Observable<boolean> {
        return new Observable<boolean>(
            observable => {
                const params = new HttpParams({ fromString: `centerToken=${publicToken}` });
                this.get('public/fees', this.defaultEndpointParams, params).subscribe(
                    data => {
                        this.dataStore.fees = [];
                        data.forEach((fee: any) => {
                            if (fee.type === FeeType.oneTime) {
                                this.dataStore.fees.push(new OneTimeFee().build(fee));
                            } else if (fee.type === FeeType.subscription) {
                                this.dataStore.fees.push(new SubscriptionPlan().build(fee));
                            }
                        });

                        this.dataStore.selected = { idFee: '', idPrice: '' };
                        observable.next(true);
                    }, () => observable.next(false));
            });
    }

    private next(): void {
        this.feesSubject.next([ ...this.dataStore.fees ]);
    }

    private loading(value: boolean): void {
        this.isLoadingDataSubject.next(value);
    }
}
